import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect';

/** Class the PostCSS `scopeToComponent` plugin scopes every rule under. */
export const SCOPE_CLASS = 'vdp-hanko-menu';

/**
 * Modal never carries data-rootownerid (only Menu does), so ownership can't
 * be checked the same way. Pass this as Modal's `className` prop to mark it
 * as ours.
 */
export const MODAL_MARKER = 'vdp-hanko-menu-modal';

/**
 * Every id that could name the trigger a Base UI popup belongs to. All matching
 * descendants are read and `aria-labelledby` is split, because any token may be
 * the trigger: `docs/SIZING.md#ownership-and-why-it-fails-closed`.
 */
function ownershipIds(node: HTMLElement): string[] {
  const descendants = node.querySelectorAll<HTMLElement>('[data-rootownerid], [aria-labelledby]');
  const raw = [
    node.dataset.rootownerid,
    node.getAttribute('aria-labelledby'),
    ...[...descendants].flatMap((el) => [
      el.dataset.rootownerid,
      el.getAttribute('aria-labelledby'),
    ]),
  ];
  return raw.flatMap((value) => value?.trim().split(/\s+/).filter(Boolean) ?? []);
}

/**
 * Tags Rockhopper's portalled Menu and Modal containers with the scope class, so
 * the scoped rules and the rewritten token block reach popups React portals to
 * `document.body`.
 *
 * Exists because `Menu.Content` hardcodes `container={undefined}` on Base UI's
 * Portal. If Rockhopper adds a portal `container` prop, delete this hook and
 * pass the scope root. See `docs/SIZING.md#reaching-portalled-popups`.
 *
 * @param enabled - only observe while a portal can exist (i.e. something is open)
 * @param ownerRoot - element whose triggers own the portals. Nothing is tagged
 *   until it resolves, since ownership can't be established without it.
 */
export function usePortalScope(enabled: boolean, ownerRoot: () => Element | null): void {
  // Layout effect, not `useEffect`: the scope class carries the sizing base and
  // React inserts the portal during commit, so tagging after paint shows one
  // frame of the popup at the host's font size.
  useIsomorphicLayoutEffect(() => {
    if (!enabled || typeof MutationObserver !== 'function') return;

    const scope = (node: HTMLElement) => {
      const isPositioner = node.classList.contains('vai-menu-content__positioner');
      const isModal = node.classList.contains('vai-modal');
      if (!isPositioner && !isModal) return;
      if (node.classList.contains(SCOPE_CLASS)) return;

      if (isModal) {
        // No data-rootownerid on Modal — only scope ones we've marked ourselves.
        if (!node.classList.contains(MODAL_MARKER)) return;
      } else {
        // Only tag a popup one of our own triggers opened — this class carries
        // the sizing base, so it resizes what it touches. Both signals are
        // needed, and the check fails closed:
        // docs/SIZING.md#ownership-and-why-it-fails-closed
        const root = ownerRoot();
        if (!root) return;

        const owners = ownershipIds(node)
          .map((id) => document.getElementById(id))
          .filter((el): el is HTMLElement => el !== null);
        if (!owners.some((owner) => root.contains(owner))) return;
      }

      node.classList.add(SCOPE_CLASS);
    };

    // Portals opened before the observer attached.
    document
      .querySelectorAll<HTMLElement>('.vai-menu-content__positioner, .vai-modal')
      .forEach(scope);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node instanceof HTMLElement) scope(node);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [enabled, ownerRoot]);
}
